import React, { useEffect, useRef } from 'react';

// ── THE DRIFT: a cloud of particles falling into the mark ────────────────────
// The absorber's desk is not a grid here: it is a slow sea of motes, each one
// orbiting the nucleus and sinking toward it, faster the closer it gets,
// fading as it nears the centre until the mark swallows it. New motes drift
// in from beyond the page's rim, so it never runs dry and never piles up.
//
//   the orbit  every mote turns about the nucleus the way the figure turns,
//              quicker near the centre (roughly Keplerian), still at the rim
//   the fall   and sinks inward all the while: a crawl at the rim, a slide
//              near the figure, gone at the horizon
//   the sea    a slow, drifting current (a few long sine waves over space and
//              time) and each mote's own temper keep it from reading as a
//              machine: it is weather, not a pattern
//
// It moves with the mark as one body. The mark's spin, wake and absorb are
// not read from it (the kit keeps them private): they are integrated here
// from the same live props with the same constants as LivingMark.jsx, so the
// two stay in step. Awake, the motes are pulled harder; absorbing, they turn
// with the figure's spin and pour in while the horizon opens. If the kit's
// LivingMark changes its easing, mirror it in `follow` below.

const SPACING = 22;   // px: one mote per SPACING² of area, about the desk grid's density
const WHIP = 0.35;    // how much of the mark's wake/absorb spin the orbit takes on
const FALL = 3;       // px/s inward at the figure's rim, at rest
const RUSH = 1.5;     // the extra speed a mote gathers on its approach to the hole
const BIRTH = 2.2;    // s: a new mote fades in over this
const BUCKETS = 10;   // alpha steps: a frame is a handful of strokes, not thousands

const smooth = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

// the mark's own props, mirrored: state, progress, pace, turn, scale
// `drift` is the mark's own resting turn: the motes at its string tips orbit
// at exactly that rate, so the figure and the field turn together
export default function VoidWarp({ host, center, state = 'idle', progress = 0, pace = 1, turn = 0.45, drift = 0, scale = 0.72 }) {
  const canvasRef = useRef(null);
  const live = useRef({});
  live.current = { state, progress, pace, turn, drift, scale };

  useEffect(() => {
    const canvas = canvasRef.current;
    const desk = host.current;
    if (!canvas || !desk) return undefined;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // motes, packed: r, θ, temper (speed 0.7–1.3), age
    let R = new Float32Array(0), T = new Float32Array(0), K = new Float32Array(0), A = new Float32Array(0);
    let count = 0;
    let W = 0, H = 0, left = 0, top = 0, dpr = 1;
    let ink = 'rgb(17 17 17 / .14)';
    let cx = 0, cy = 0, S = 400, rim = 1000; // the hole, the figure, the farthest a mote lives

    const locate = () => {
      const mark = desk.querySelector('.ofself-living-mark');
      const mr = mark?.getBoundingClientRect();
      const c = center();
      cx = c ? c.x - left : W / 2;
      cy = c ? c.y - top : H / 2;
      S = mr ? Math.min(mr.width, mr.height) * (live.current.scale || 0.72) : Math.min(W, H) * 0.6;
      rim = Math.hypot(Math.max(cx, W - cx), Math.max(cy, H - cy)) + SPACING * 2;
    };
    // a mote placed uniformly by area over the ring [r0, rim]
    const seed = (i, r0, age) => {
      const u = Math.random();
      R[i] = Math.sqrt(r0 * r0 + u * (rim * rim - r0 * r0));
      T[i] = Math.random() * Math.PI * 2;
      K[i] = 0.7 + Math.random() * 0.6;
      A[i] = age;
    };
    const size = () => {
      const r = desk.getBoundingClientRect();
      dpr = Math.min(2, window.devicePixelRatio || 1);
      W = r.width; H = r.height; left = r.left; top = r.top;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      const css = getComputedStyle(desk);
      const inkRgb = css.getPropertyValue('--ofself-ink-rgb').trim() || '17 17 17';
      ink = css.getPropertyValue('--ofself-desk-dot').trim() || `rgb(${inkRgb} / .14)`;
      locate();
      // enough motes to fill the disc out to the rim; a bigger room adds some
      const want = Math.round((Math.PI * rim * rim) / (SPACING * SPACING));
      if (want > count) {
        const grow = (a) => { const b = new Float32Array(want); b.set(a); return b; };
        R = grow(R); T = grow(T); K = grow(K); A = grow(A);
        // the first fill is already mature (the sea is there on arrival); later ones are born
        for (let i = count; i < want; i++) seed(i, 0, count ? 0 : BIRTH + Math.random() * 10);
        count = want;
      }
    };
    size();
    const ro = new ResizeObserver(size);
    ro.observe(desk);
    // the skin flips the ink; re-read it
    const mo = new MutationObserver(size);
    mo.observe(document.documentElement, { attributes: true });

    // ── the mark's motion, integrated exactly as LivingMark does ──
    const m = { wake: 0, absorb: 0, spin: 0 };
    const follow = (dt) => {
      const L = live.current;
      m.wake += ((L.state === 'wake' ? 1 : 0) - m.wake) * (1 - Math.exp(-dt * (L.state === 'wake' ? 6 : 2.5)));
      const aTarget = L.state === 'absorb' ? Math.max(0, Math.min(1, L.progress || 0)) : 0;
      m.absorb += (aTarget - m.absorb) * (1 - Math.exp(-dt * 5 * (L.pace || 1)));
      const spinTarget = reduce ? 0 : (L.drift || 0) + (L.state === 'absorb' ? 2.4 * Math.sqrt(L.pace || 1) : (L.turn ?? 0.45) * m.wake);
      m.spin += (spinTarget - m.spin) * (1 - Math.exp(-dt * 1.8 * (L.pace || 1)));
    };

    const buckets = Array.from({ length: BUCKETS }, () => []);
    const t0 = performance.now();
    let last = t0;
    let raf = 0;

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = (now - t0) / 1000;
      follow(dt);
      locate();
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      if (document.documentElement.dataset.deskDots === 'off') return;

      const collapse = smooth(0, 1, m.absorb);
      const drift = reduce ? 0 : live.current.drift || 0;
      const fall = reduce ? 0 : FALL * (1 + 1.6 * m.wake + 7 * collapse);
      const horizon = S * (0.07 + 0.14 * collapse);
      const core = S * 0.25; // softens the speed-up so nothing whips at the very centre
      // the orbit's shape over radius: a planet's fall-off (speed ∝ 1/√r),
      // quickening on the approach to the hole (up to 1 + RUSH at the centre)
      const shape = (r, g) => (1 + RUSH * smooth(0.7, 2.5, g)) * Math.sqrt(g) / Math.max(r, core);
      // scaled so a mote at the string tips (half the figure out) turns at the
      // mark's own drift; the rest of the mark's spin whips the near field
      const tip = S * 0.5;
      const orbit = drift * (1 + 0.6 * m.wake) / shape(tip, S / (tip + core));
      const whip = WHIP * Math.max(0, m.spin - drift);

      for (const b of buckets) b.length = 0;
      for (let i = 0; i < count; i++) {
        let r = R[i], th = T[i];
        const k = K[i];
        const x0 = cx + r * Math.cos(th), y0 = cy + r * Math.sin(th);
        // the sea: a slow current drifting over space and time
        const sea = Math.sin(x0 * 0.0041 + t * 0.13) + Math.sin(y0 * 0.0053 - t * 0.11) + Math.sin((x0 - y0) * 0.0029 + t * 0.07);
        const g = S / (r + core);                      // ~1 at the figure's rim, rising toward the centre
        const w = (orbit * k * shape(r, g) + whip * g * g) * (1 + 0.18 * sea);
        const v = fall * k * (1 + RUSH * smooth(0.7, 2.5, g)) * Math.pow(g, 0.9) * (1 + 0.35 * sea);
        th += w * dt;
        r -= v * dt;
        A[i] += dt;
        if (r < horizon * 0.5 || r > rim * 1.05) { seed(i, rim * 0.92, 0); continue; } // swallowed: a new one at the rim
        R[i] = r; T[i] = th;
        const x = cx + r * Math.cos(th), y = cy + r * Math.sin(th);
        if (x < -4 || x > W + 4 || y < -4 || y > H + 4) continue;
        const a = smooth(horizon * 0.5, horizon * 3, r) * smooth(0, BIRTH, A[i]);
        const bucket = Math.min(BUCKETS - 1, Math.floor(a * BUCKETS));
        if (bucket <= 0) continue;
        buckets[bucket].push(x, y);
      }

      ctx.strokeStyle = ink;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      for (let b = 1; b < BUCKETS; b++) {
        const list = buckets[b];
        if (!list.length) continue;
        ctx.globalAlpha = (b + 1) / BUCKETS;
        ctx.beginPath();
        // each mote a round dot the size of the desk's own (a nudged
        // zero-length stroke: the round cap paints it, one path per bucket)
        for (let n = 0; n < list.length; n += 2) {
          ctx.moveTo(list[n], list[n + 1]);
          ctx.lineTo(list[n] + 0.01, list[n + 1]);
        }
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
    };
  }, [host, center]);

  return <canvas ref={canvasRef} className="absorber-void" aria-hidden="true" />;
}

// ── "void" — type it on the absorber and the mark swallows its own desk ──
// Returns the absorb progress to lend the mark while it plays (null at rest):
// in over 2.4s, held, out again, so the figure collapses and regrows and the
// motes pour in with it.
const TRIGGER = 'void';
const IN_MS = 2400, HOLD_MS = 1400, OUT_MS = 2600;
export function useVoidEgg(enabled) {
  const [p, setP] = React.useState(null);
  const running = useRef(false);
  useEffect(() => {
    if (!enabled) return undefined;
    let typed = '';
    let raf = 0;
    const play = () => {
      running.current = true;
      const start = performance.now();
      const tick = (now) => {
        const ms = now - start;
        if (ms >= IN_MS + HOLD_MS + OUT_MS) { running.current = false; setP(null); return; }
        setP(ms < IN_MS ? smooth(0, IN_MS, ms)
          : ms < IN_MS + HOLD_MS ? 1
            : 1 - smooth(0, OUT_MS, ms - IN_MS - HOLD_MS));
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.key.length !== 1 || running.current) return;
      typed = (typed + e.key.toLowerCase()).slice(-TRIGGER.length);
      if (typed === TRIGGER) { typed = ''; play(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(raf);
      running.current = false;
    };
  }, [enabled]);
  return p;
}
